import { useState } from 'react'
import { keepPreviousData } from '@tanstack/react-query'
import { useDebounce } from '@uidotdev/usehooks'
import { useHotkey } from '@tanstack/react-hotkeys'
import type { components } from '@joyful/openapi/types.gen'
import {
  formatDate,
  formatDateRange,
  formatDuration,
  formatE164PhoneNumber,
  formatTimestamp,
} from '@joyful/utils'
import {
  ArrowLeftIcon,
  ChartLineIcon,
  LoaderCircleIcon,
  PhoneIncomingIcon,
  PhoneOutgoingIcon,
  SearchIcon,
} from 'lucide-react'

import {
  CallStatusBadge,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandHeader,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  Ellipsis,
  IconButton,
  PracticeRequestStatusBadge,
  PracticeRequestTypeTag,
  ProjectStatusBadge,
  Redact,
  Stack,
  Tabular,
  getPracticeRequestDisplayText,
} from '../../../core'
import { useHasUserCapability } from '../../hooks/useHasUserCapability'
import { EncounterCommandItem } from '../../features/encounters/components/EncounterCommandItem'
import { useEncounterSearch } from '../../features/encounters/hooks/useEncounterSearch'
import {
  useCanOpenPracticeEncounterDetails,
  useCanOpenWorkspaceEncounterDetails,
  usePracticeEncounterHrefs,
} from '../../features/encounters/utils/appEncounterHrefs'
import { getWorkspaceHref } from '../../features/encounters/utils/workspaceEncounterHrefs'
import { KpiEmailStateBadge } from '../../features/kpi-emails/components/KpiEmailStateBadge'
import { serializePhoneCallsRouteQueryParams } from '../../features/phone/utils/phoneQueryParams'
import { serializePracticeRequestNavigationSource } from '../../features/requests/hooks/usePracticeRequestNavigationSource'
import { getCurrentNavbarApp, getNavbarAppHref } from '../navbar/Navbar'
import { useApiOrgSlug } from '../../utils/orgSlug'
import { useApiQuery } from '../../utils/reactQuery'
import { AnalysisPageEntitySearchResults } from './GlobalCommandAnalysisPageSearch'

const ENTITY_SEARCH_PAGE_SIZE = 20
const PRACTICE_REQUEST_PREVIEW_LENGTH = 80

export type GlobalCommandEntitySearchScope =
  | 'analysis_pages'
  | 'encounters'
  | 'kpi_emails'
  | 'patients'
  | 'phone_calls'
  | 'practice_requests'
  | 'projects'

const ENTITY_SEARCH_SCOPE_META: Record<
  GlobalCommandEntitySearchScope,
  {
    heading: string
    placeholder: string
    searchLabel: string
  }
> = {
  analysis_pages: {
    heading: `Analysis pages`,
    placeholder: `Search analysis pages…`,
    searchLabel: `Search analysis pages…`,
  },
  encounters: {
    heading: `Encounters`,
    placeholder: `Search encounters…`,
    searchLabel: `Search encounters…`,
  },
  kpi_emails: {
    heading: `KPI emails`,
    placeholder: `Search KPI emails…`,
    searchLabel: `Search KPI emails…`,
  },
  patients: {
    heading: `Patients`,
    placeholder: `Search patients…`,
    searchLabel: `Search patients…`,
  },
  phone_calls: {
    heading: `Phone calls`,
    placeholder: `Search phone calls…`,
    searchLabel: `Search phone calls…`,
  },
  practice_requests: {
    heading: `Practice requests`,
    placeholder: `Search practice requests…`,
    searchLabel: `Search practice requests…`,
  },
  projects: {
    heading: `Projects`,
    placeholder: `Search projects…`,
    searchLabel: `Search projects…`,
  },
}

export interface GlobalCommandEntitySearchMenuProps {
  onSelectScope: (scope: GlobalCommandEntitySearchScope) => void
}

export function GlobalCommandEntitySearchMenu({
  onSelectScope,
}: GlobalCommandEntitySearchMenuProps) {
  const canViewEncounters = useHasUserCapability('encounter.list.view')
  const canViewPhone = useHasUserCapability('phone.view')
  const canViewKpiEmails = useHasUserCapability('kpi_email.view')
  const canViewAnalysisPages = useHasUserCapability('analysis.view')
  const canOpenPracticeEncounterDetails = useCanOpenPracticeEncounterDetails()
  const canOpenWorkspaceEncounterDetails = useCanOpenWorkspaceEncounterDetails()
  const canSearchEncounterDetails =
    canOpenPracticeEncounterDetails || canOpenWorkspaceEncounterDetails

  return (
    <CommandGroup heading={`Search`}>
      {canViewEncounters && canSearchEncounterDetails ? (
        <CommandItem
          size="xl"
          animation="none"
          keywords={[`encounter`, `encounters`, `search encounters`]}
          startAdornment={<SearchIcon className="size-4" />}
          onSelect={() => {
            onSelectScope('encounters')
          }}
        >
          {ENTITY_SEARCH_SCOPE_META.encounters.searchLabel}
        </CommandItem>
      ) : null}

      {canViewEncounters ? (
        <CommandItem
          size="xl"
          animation="none"
          keywords={[`patient`, `patients`, `search patients`]}
          startAdornment={<SearchIcon className="size-4" />}
          onSelect={() => {
            onSelectScope('patients')
          }}
        >
          {ENTITY_SEARCH_SCOPE_META.patients.searchLabel}
        </CommandItem>
      ) : null}

      <CommandItem
        size="xl"
        animation="none"
        keywords={[`project`, `projects`, `search projects`]}
        startAdornment={<SearchIcon className="size-4" />}
        onSelect={() => {
          onSelectScope('projects')
        }}
      >
        {ENTITY_SEARCH_SCOPE_META.projects.searchLabel}
      </CommandItem>

      {canViewAnalysisPages ? (
        <CommandItem
          size="xl"
          animation="none"
          keywords={[
            `analysis`,
            `analyses`,
            `analysis pages`,
            `search analysis pages`,
          ]}
          startAdornment={<ChartLineIcon className="size-4" />}
          onSelect={() => {
            onSelectScope('analysis_pages')
          }}
        >
          {ENTITY_SEARCH_SCOPE_META.analysis_pages.searchLabel}
        </CommandItem>
      ) : null}

      <CommandItem
        size="xl"
        animation="none"
        keywords={[
          `practice request`,
          `practice requests`,
          `request`,
          `requests`,
          `search practice requests`,
        ]}
        startAdornment={<SearchIcon className="size-4" />}
        onSelect={() => {
          onSelectScope('practice_requests')
        }}
      >
        {ENTITY_SEARCH_SCOPE_META.practice_requests.searchLabel}
      </CommandItem>

      {canViewPhone ? (
        <CommandItem
          size="xl"
          animation="none"
          keywords={[`phone`, `call`, `phone calls`, `search phone calls`]}
          startAdornment={<SearchIcon className="size-4" />}
          onSelect={() => {
            onSelectScope('phone_calls')
          }}
        >
          {ENTITY_SEARCH_SCOPE_META.phone_calls.searchLabel}
        </CommandItem>
      ) : null}

      {canViewKpiEmails ? (
        <CommandItem
          size="xl"
          animation="none"
          keywords={[`kpi`, `email`, `kpi emails`, `search kpi emails`]}
          startAdornment={<SearchIcon className="size-4" />}
          onSelect={() => {
            onSelectScope('kpi_emails')
          }}
        >
          {ENTITY_SEARCH_SCOPE_META.kpi_emails.searchLabel}
        </CommandItem>
      ) : null}
    </CommandGroup>
  )
}

export interface GlobalCommandEntitySearchProps {
  onGoBack: () => void
  onSelect: () => void
  scope: GlobalCommandEntitySearchScope
}

function entitySearchUsesServerQuery(scope: GlobalCommandEntitySearchScope) {
  return (
    scope === 'encounters' ||
    scope === 'patients' ||
    scope === 'practice_requests' ||
    scope === 'projects'
  )
}

export function GlobalCommandEntitySearch({
  onGoBack,
  onSelect,
  scope,
}: GlobalCommandEntitySearchProps) {
  const [searchValue, setSearchValue] = useState('')
  const scopeMeta = ENTITY_SEARCH_SCOPE_META[scope]
  const shouldFilter = !entitySearchUsesServerQuery(scope)

  useHotkey('ArrowLeft', onGoBack, { ignoreInputs: false })

  return (
    <Command shouldFilter={shouldFilter} loop>
      <CommandHeader
        startAdornment={
          <IconButton
            size="lg"
            colorVariant="muted"
            variant="ghost"
            tooltip={{
              content: `Back`,
              hotkey: `ArrowLeft`,
            }}
            onClick={onGoBack}
          >
            <ArrowLeftIcon />
          </IconButton>
        }
      >
        {scopeMeta.heading}
      </CommandHeader>

      <CommandInput
        autoFocus
        placeholder={scopeMeta.placeholder}
        startIcon={null}
        size="lg"
        value={searchValue}
        onValueChange={setSearchValue}
      />

      <CommandList className="max-h-112">
        {scope === 'encounters' ? (
          <EncounterEntitySearchResults
            onSelect={onSelect}
            query={searchValue}
          />
        ) : null}
        {scope === 'patients' ? (
          <PatientEntitySearchResults onSelect={onSelect} query={searchValue} />
        ) : null}
        {scope === 'projects' ? (
          <ProjectEntitySearchResults onSelect={onSelect} query={searchValue} />
        ) : null}
        {scope === 'analysis_pages' ? (
          <AnalysisPageEntitySearchResults
            onSelect={onSelect}
            query={searchValue}
          />
        ) : null}
        {scope === 'practice_requests' ? (
          <PracticeRequestEntitySearchResults
            onSelect={onSelect}
            query={searchValue}
          />
        ) : null}
        {scope === 'phone_calls' ? (
          <PhoneCallEntitySearchResults
            onSelect={onSelect}
            query={searchValue}
          />
        ) : null}
        {scope === 'kpi_emails' ? (
          <KpiEmailEntitySearchResults
            onSelect={onSelect}
            query={searchValue}
          />
        ) : null}
      </CommandList>
    </Command>
  )
}

function useLoadMoreLimit(pageSize: number) {
  const [limit, setLimit] = useState(pageSize)

  const loadMore = () => {
    setLimit((currentLimit) => {
      return currentLimit + pageSize
    })
  }

  return { limit, loadMore }
}

function EntitySearchLoadMoreItem({
  isFetching,
  onLoadMore,
}: {
  isFetching: boolean
  onLoadMore: () => void
}) {
  return (
    <CommandItem
      size="sm"
      animation="none"
      value="__load-more__"
      forceMount
      className="text-tremor-brand justify-center text-xs font-medium"
      disabled={isFetching}
      startAdornment={
        isFetching ? (
          <LoaderCircleIcon className="size-3.5 animate-spin" />
        ) : null
      }
      onSelect={onLoadMore}
    >
      {isFetching ? `Loading…` : `Show more results`}
    </CommandItem>
  )
}

function EncounterEntitySearchResults({
  onSelect,
  query,
}: {
  onSelect: () => void
  query: string
}) {
  const orgSlug = useApiOrgSlug()
  const { getPracticeEncounterHref } = usePracticeEncounterHrefs()
  const { limit, loadMore } = useLoadMoreLimit(ENTITY_SEARCH_PAGE_SIZE)
  const encounterSearch = useEncounterSearch({
    limit,
    query: query.trim(),
  })

  if (encounterSearch.isLoading) {
    return <CommandLoading>{`Loading…`}</CommandLoading>
  }

  if (!encounterSearch.isSuccess) {
    return null
  }

  const navigableEncounters = encounterSearch.encounters.flatMap(
    (encounter) => {
      const encounterHref = getPracticeEncounterHref({
        encounterId: encounter.id,
        orgSlug,
      })
      if (encounterHref === undefined) {
        return []
      }

      return [{ encounter, encounterHref }]
    },
  )

  if (navigableEncounters.length === 0) {
    return (
      <CommandEmpty className="text-tremor-content p-2.5 text-center text-xs">
        {`No encounters found.`}
      </CommandEmpty>
    )
  }

  return (
    <CommandGroup forceMount>
      {navigableEncounters.map(({ encounter, encounterHref }) => {
        return (
          <EncounterCommandItem
            key={encounter.id}
            animation="none"
            account={encounter.account}
            encounter={encounter}
            onSelect={() => {
              onSelect()
              globalThis.location.assign(encounterHref)
            }}
          />
        )
      })}
      {encounterSearch.hasMore ? (
        <EntitySearchLoadMoreItem
          isFetching={encounterSearch.isLoading}
          onLoadMore={loadMore}
        />
      ) : null}
    </CommandGroup>
  )
}

function PatientEntitySearchResults({
  onSelect,
  query,
}: {
  onSelect: () => void
  query: string
}) {
  const orgSlug = useApiOrgSlug()
  const { limit, loadMore } = useLoadMoreLimit(ENTITY_SEARCH_PAGE_SIZE)
  const debouncedQuery = useDebounce(query, 100)
  const patientsQuery = useApiQuery(
    'get',
    '/{org_slug}/persons',
    {
      params: {
        path: {
          org_slug: orgSlug,
        },
        query: {
          limit,
          page: 1,
          query: debouncedQuery.trim(),
          sort_key: 'patient_name',
          sort_order: 'asc',
        },
      },
    },
    {
      placeholderData: keepPreviousData,
    },
  )
  const isLoading =
    query.trim() !== debouncedQuery.trim() || patientsQuery.isLoading

  if (isLoading) {
    return <CommandLoading>{`Loading…`}</CommandLoading>
  }

  if (patientsQuery.isError) {
    return (
      <CommandEmpty className="text-tremor-content p-2.5 text-center text-xs">
        {`Unable to load patients.`}
      </CommandEmpty>
    )
  }

  if (!patientsQuery.isSuccess) {
    return null
  }

  if (patientsQuery.data.patients.length === 0) {
    return (
      <CommandEmpty className="text-tremor-content p-2.5 text-center text-xs">
        {`No patients found.`}
      </CommandEmpty>
    )
  }

  return (
    <CommandGroup forceMount>
      {patientsQuery.data.patients.map((patient) => {
        const patientName = getPatientDisplayName(patient)
        return (
          <CommandItem
            key={patient.id}
            size="xl"
            animation="none"
            value={patient.id}
            keywords={[patientName]}
            onSelect={() => {
              onSelect()
              globalThis.location.assign(
                getNavbarAppHref({
                  app: 'app',
                  path: `/${orgSlug}/patients/${patient.id}`,
                }),
              )
            }}
          >
            <Redact>{patientName}</Redact>
          </CommandItem>
        )
      })}
      {patientsQuery.data.has_more ? (
        <EntitySearchLoadMoreItem
          isFetching={patientsQuery.isFetching}
          onLoadMore={loadMore}
        />
      ) : null}
    </CommandGroup>
  )
}

function getPatientDisplayName(
  patient: Pick<
    components['schemas']['PersonListItem'],
    'first_name' | 'full_name' | 'last_name'
  >,
) {
  if (patient.full_name) {
    return patient.full_name
  }

  const patientName = [patient.first_name, patient.last_name]
    .filter(Boolean)
    .join(' ')

  return patientName || `Unknown patient`
}

function ProjectEntitySearchResults({
  onSelect,
  query,
}: {
  onSelect: () => void
  query: string
}) {
  const orgSlug = useApiOrgSlug()
  const { limit, loadMore } = useLoadMoreLimit(ENTITY_SEARCH_PAGE_SIZE)
  const debouncedQuery = useDebounce(query, 100)
  const projectsQuery = useApiQuery(
    'get',
    '/{org_slug}/projects',
    {
      params: {
        path: {
          org_slug: orgSlug,
        },
        query: {
          include_archived: true,
          limit,
          query: debouncedQuery.trim(),
        },
      },
    },
    { placeholderData: keepPreviousData },
  )
  const isLoading =
    query.trim() !== debouncedQuery.trim() || projectsQuery.isLoading
  const hasMore = projectsQuery.isSuccess && projectsQuery.data.length >= limit

  if (isLoading) {
    return <CommandLoading>{`Loading…`}</CommandLoading>
  }

  if (!projectsQuery.isSuccess) {
    return null
  }

  if (projectsQuery.data.length === 0) {
    return (
      <CommandEmpty className="text-tremor-content p-2.5 text-center text-xs">
        {`No projects found.`}
      </CommandEmpty>
    )
  }

  return (
    <CommandGroup forceMount>
      {projectsQuery.data.map((project) => {
        return (
          <CommandItem
            key={project.id}
            size="xl"
            animation="none"
            value={project.id}
            keywords={[project.title]}
            endAdornment={
              <ProjectStatusBadge size="sm" status={project.status} />
            }
            subtitle={project.description}
            onSelect={() => {
              onSelect()
              globalThis.location.assign(
                getNavbarAppHref({
                  app:
                    getCurrentNavbarApp() === 'workspace' ? 'workspace' : 'app',
                  path: `/${orgSlug}/projects/${project.id}`,
                }),
              )
            }}
          >
            {project.title}
          </CommandItem>
        )
      })}
      {hasMore ? (
        <EntitySearchLoadMoreItem
          isFetching={projectsQuery.isFetching}
          onLoadMore={loadMore}
        />
      ) : null}
    </CommandGroup>
  )
}

function PracticeRequestEntitySearchResults({
  onSelect,
  query,
}: {
  onSelect: () => void
  query: string
}) {
  const orgSlug = useApiOrgSlug()
  const { limit, loadMore } = useLoadMoreLimit(ENTITY_SEARCH_PAGE_SIZE)
  const debouncedQuery = useDebounce(query, 100)
  const requestsQuery = useApiQuery(
    'get',
    '/{org_slug}/requests',
    {
      params: {
        path: {
          org_slug: orgSlug,
        },
        query: {
          include_closed: true,
          limit,
          query: debouncedQuery.trim() || undefined,
        },
      },
    },
    { placeholderData: keepPreviousData },
  )
  const isLoading =
    query.trim() !== debouncedQuery.trim() || requestsQuery.isLoading
  const requestItems = requestsQuery.data?.items ?? []
  const hasMore =
    requestsQuery.isSuccess && requestsQuery.data.total_count > limit

  if (isLoading) {
    return <CommandLoading>{`Loading…`}</CommandLoading>
  }

  if (!requestsQuery.isSuccess) {
    return null
  }

  if (requestItems.length === 0) {
    return (
      <CommandEmpty className="text-tremor-content p-2.5 text-center text-xs">
        {`No practice requests found.`}
      </CommandEmpty>
    )
  }

  return (
    <CommandGroup forceMount>
      {requestItems.map((request) => {
        const { primaryText, source } = getPracticeRequestDisplayText(request)
        const label = getPracticeRequestPreview(primaryText)
        const hasRequestText = source !== 'placeholder'
        return (
          <CommandItem
            key={request.id}
            size="xl"
            animation="none"
            value={request.id}
            keywords={[label, request.title ?? '', request.description ?? '']}
            onSelect={() => {
              onSelect()
              const practiceRequestPath =
                serializePracticeRequestNavigationSource(
                  `/${orgSlug}/requests/${request.id}`,
                  { source: 'requests' },
                )
              globalThis.location.assign(
                getNavbarAppHref({
                  app: 'app',
                  path: practiceRequestPath,
                }),
              )
            }}
          >
            <Stack gap="xs" grow="fill">
              <Stack direction="horizontal" align="center" gap="xs">
                <PracticeRequestStatusBadge size="sm" status={request.status} />
                <PracticeRequestTypeTag
                  requestType={request.request_type}
                  size="sm"
                />
              </Stack>
              <Ellipsis numLines={2} size="sm" weight="medium">
                {hasRequestText ? (
                  <Redact>{label}</Redact>
                ) : (
                  <span className="text-muted italic">{label}</span>
                )}
              </Ellipsis>
            </Stack>
          </CommandItem>
        )
      })}
      {hasMore ? (
        <EntitySearchLoadMoreItem
          isFetching={requestsQuery.isFetching}
          onLoadMore={loadMore}
        />
      ) : null}
    </CommandGroup>
  )
}

function getPracticeRequestPreview(description: string) {
  const normalized = description.split(/\s+/).join(' ')
  if (normalized.length <= PRACTICE_REQUEST_PREVIEW_LENGTH) {
    return normalized
  }

  return `${normalized.slice(0, PRACTICE_REQUEST_PREVIEW_LENGTH).trimEnd()}…`
}

function PhoneCallEntitySearchResults({
  onSelect,
}: {
  onSelect: () => void
  query: string
}) {
  const orgSlug = useApiOrgSlug()
  const { limit, loadMore } = useLoadMoreLimit(ENTITY_SEARCH_PAGE_SIZE)
  const callsQuery = useApiQuery(
    'get',
    '/phone/{org_slug}/calls',
    {
      params: {
        path: {
          org_slug: orgSlug,
        },
        query: {
          limit,
          page: 1,
        },
      },
    },
    {
      placeholderData: keepPreviousData,
    },
  )
  const hasMore =
    callsQuery.isSuccess &&
    callsQuery.data.items.length < callsQuery.data.total_count

  if (callsQuery.isLoading) {
    return <CommandLoading>{`Loading…`}</CommandLoading>
  }

  if (!callsQuery.isSuccess) {
    return null
  }

  if (callsQuery.data.items.length === 0) {
    return (
      <CommandEmpty className="text-tremor-content p-2.5 text-center text-xs">
        {`No phone calls found.`}
      </CommandEmpty>
    )
  }

  return (
    <CommandGroup forceMount>
      {callsQuery.data.items.map((call) => {
        const phoneNumber = formatE164PhoneNumber(call.phone_number)
        return (
          <CommandItem
            key={call.id}
            size="xl"
            animation="none"
            value={call.id}
            keywords={[
              call.phone_number,
              call.status,
              call.direction,
              call.summary,
              phoneNumber,
            ].filter(Boolean)}
            startAdornment={
              call.direction === 'outbound' ? (
                <PhoneOutgoingIcon className="size-3.5" />
              ) : (
                <PhoneIncomingIcon className="size-3.5" />
              )
            }
            endAdornment={<CallStatusBadge size="sm" status={call.status} />}
            subtitle={[
              call.created_at !== null &&
                formatTimestamp(call.created_at, {
                  format: 'adaptive',
                }),
              call.duration_seconds && formatDuration(call.duration_seconds),
            ]
              .filter(Boolean)
              .join(' · ')}
            onSelect={() => {
              onSelect()
              const phoneCallsPath = serializePhoneCallsRouteQueryParams(
                `/${orgSlug}/phone/calls`,
                { callId: call.id },
              )
              globalThis.location.assign(getWorkspaceHref(phoneCallsPath))
            }}
          >
            <Tabular>{phoneNumber}</Tabular>
          </CommandItem>
        )
      })}
      {hasMore ? (
        <EntitySearchLoadMoreItem
          isFetching={callsQuery.isFetching}
          onLoadMore={loadMore}
        />
      ) : null}
    </CommandGroup>
  )
}

function KpiEmailEntitySearchResults({
  onSelect,
}: {
  onSelect: () => void
  query: string
}) {
  const orgSlug = useApiOrgSlug()
  const { limit, loadMore } = useLoadMoreLimit(ENTITY_SEARCH_PAGE_SIZE)
  const historyQuery = useApiQuery(
    'get',
    '/{org_slug}/kpi-emails',
    {
      params: {
        path: {
          org_slug: orgSlug,
        },
        query: {
          limit,
          page: 1,
        },
      },
    },
    {
      placeholderData: keepPreviousData,
    },
  )
  const hasMore =
    historyQuery.isSuccess &&
    historyQuery.data.items.length < historyQuery.data.total_count

  if (historyQuery.isLoading) {
    return <CommandLoading>{`Loading…`}</CommandLoading>
  }

  if (!historyQuery.isSuccess) {
    return null
  }

  if (historyQuery.data.items.length === 0) {
    return (
      <CommandEmpty className="text-tremor-content p-2.5 text-center text-xs">
        {`No KPI emails found.`}
      </CommandEmpty>
    )
  }

  return (
    <CommandGroup forceMount>
      {historyQuery.data.items.map((email) => {
        const title = getKpiEmailTitle(email)
        return (
          <CommandItem
            key={email.id}
            size="xl"
            animation="none"
            value={email.id}
            keywords={[title, email.subject ?? '', email.state]}
            endAdornment={
              <KpiEmailStateBadge
                size="sm"
                state={email.state}
                skippedAt={email.skipped_at}
                skippedReason={email.skipped_reason}
              />
            }
            subtitle={formatDateRange(email.period_start, email.period_end, {
              shouldSkipCurrentYear: true,
            })}
            onSelect={() => {
              onSelect()
              globalThis.location.assign(
                getNavbarAppHref({
                  app: 'internal',
                  path: `/${orgSlug}/internal/kpi-emails/${email.id}`,
                }),
              )
            }}
          >
            {title}
          </CommandItem>
        )
      })}
      {hasMore ? (
        <EntitySearchLoadMoreItem
          isFetching={historyQuery.isFetching}
          onLoadMore={loadMore}
        />
      ) : null}
    </CommandGroup>
  )
}

function getKpiEmailTitle(
  email: Pick<components['schemas']['KpiEmailListItemResponse'], 'period_end'>,
) {
  return `${formatDate(email.period_end, { shouldSkipCurrentYear: true })} KPI email`
}
